// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { SettingsCompareIcon } from "@/components/icons";
import { VirtualSelectPopover } from "@/components/shared/virtual-select-popover";
import { WizardPane } from "@/components/shared/wizard-pane";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { useBackofficeContext } from "@/hooks/use-backoffice-context";

interface CompareChainDialogProps {
  defaultChainName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CompareChainDialog({ defaultChainName, open, onOpenChange }: CompareChainDialogProps) {
  const { t } = useTranslation("back-office");
  const navigate = useNavigate();
  const [chainNameA, setChainNameA] = useState(defaultChainName);
  const [chainNameB, setChainNameB] = useState("");

  const prevOpen = useRef(false);
  useEffect(
    function resetSelectionsOnOpen() {
      if (open && !prevOpen.current) {
        setChainNameA(defaultChainName);
        setChainNameB("");
      }
      prevOpen.current = open;
    },
    [open, defaultChainName],
  );

  const { data: context } = useBackofficeContext();
  const allOptions = useMemo(
    () => (context?.chains ?? []).map((c) => ({ label: c.display, value: c.value })),
    [context],
  );

  const optionsA = useMemo(() => allOptions.filter((o) => o.value !== chainNameB), [allOptions, chainNameB]);
  const optionsB = useMemo(() => allOptions.filter((o) => o.value !== chainNameA), [allOptions, chainNameA]);

  function handleCompare() {
    if (!chainNameA || !chainNameB) return;
    navigate({
      to: "/chains/compare/$chainNameA/$chainNameB",
      params: { chainNameA, chainNameB },
    });
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent id="dialog-compare-chains" className="sm:max-w-[37rem] p-0 gap-0 overflow-hidden flex flex-col">
        <div className="flex flex-row">
          <WizardPane icon={SettingsCompareIcon} />
          <div className="flex-1 p-6 flex flex-col gap-4 min-w-0">
            <DialogHeader>
              <DialogTitle>{t("chains.compare.dialog.title")}</DialogTitle>
              <DialogDescription>
                {t("chains.compare.dialog.description", "Select two chains to compare their settings.")}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">{t("chains.compare.dialog.sourceLabel")}</Label>
                <VirtualSelectPopover
                  value={chainNameA}
                  onChange={setChainNameA}
                  options={optionsA}
                  placeholder={t("chains.compare.dialog.pickChain")}
                  searchPlaceholder={t("chains.list.searchPlaceholder")}
                  emptyText={t("common.noResults")}
                />
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">{t("chains.compare.dialog.targetLabel")}</Label>
                <VirtualSelectPopover
                  value={chainNameB}
                  onChange={setChainNameB}
                  options={optionsB}
                  placeholder={t("chains.compare.dialog.pickChain")}
                  searchPlaceholder={t("chains.list.searchPlaceholder")}
                  emptyText={t("common.noResults")}
                />
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="mx-0 mb-0 rounded-none rounded-b-lg">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("buttons.cancel")}
          </Button>
          <Button onClick={handleCompare} disabled={!chainNameA || !chainNameB}>
            <SettingsCompareIcon className="h-4 w-4" />
            {t("chains.compare.dialog.compare")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
